import { getAllCountries, getAllTimezones, getTimezonesForCountry } from 'countries-and-timezones'
import countryToCurrency from 'country-to-currency'

// --- Countries (247 from ISO 3166-1) ---
const allCountries = getAllCountries()
export const COUNTRIES = Object.values(allCountries)
  .map((c) => ({ value: c.id, label: c.name }))
  .sort((a, b) => a.label.localeCompare(b.label))

// --- Timezones (all IANA, non-alias only) ---
const allTimezones = getAllTimezones()

// countries-and-timezones only knows the STANDARD offset, so DST zones show
// a wrong label half the year (America/Los_Angeles read "UTC-08:00" in June).
// Compute the offset in effect right now; fall back to the static one for
// zones the runtime's ICU build doesn't know.
function currentUtcOffsetLabel(tzName: string, fallbackOffsetStr: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tzName,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date())
    const offset = parts.find((part) => part.type === 'timeZoneName')?.value
    if (!offset) {
      return `UTC${fallbackOffsetStr}`
    }
    return offset === 'GMT' ? 'UTC+00:00' : offset.replace('GMT', 'UTC')
  } catch {
    return `UTC${fallbackOffsetStr}`
  }
}

// Lazy: the ~340-zone Intl loop costs ~10ms+ per JS context, and this module
// sits in the root-layout client graph via other constants. Only timezone
// pickers pay for it, on first use.
let cachedTimezones: Array<{ value: string; label: string }> | null = null

export function getTimezones(): Array<{ value: string; label: string }> {
  if (!cachedTimezones) {
    cachedTimezones = Object.values(allTimezones)
      .filter((tz) => !tz.aliasOf)
      .map((tz) => ({
        value: tz.name,
        label: `${tz.name.replace(/_/g, ' ')} (${currentUtcOffsetLabel(tz.name, tz.utcOffsetStr)})`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }
  return cachedTimezones
}

// --- Currencies (common ones for STR industry, from ISO 4217) ---
const CURRENCY_LABELS: Record<string, string> = {
  EUR: 'Euro',
  USD: 'US Dollar',
  GBP: 'British Pound',
  CHF: 'Swiss Franc',
  AED: 'UAE Dirham',
  JPY: 'Japanese Yen',
  AUD: 'Australian Dollar',
  CAD: 'Canadian Dollar',
  NZD: 'New Zealand Dollar',
  SEK: 'Swedish Krona',
  NOK: 'Norwegian Krone',
  DKK: 'Danish Krone',
  PLN: 'Polish Zloty',
  CZK: 'Czech Koruna',
  HUF: 'Hungarian Forint',
  RON: 'Romanian Leu',
  BGN: 'Bulgarian Lev',
  TRY: 'Turkish Lira',
  ILS: 'Israeli Shekel',
  THB: 'Thai Baht',
  MYR: 'Malaysian Ringgit',
  SGD: 'Singapore Dollar',
  HKD: 'Hong Kong Dollar',
  KRW: 'South Korean Won',
  INR: 'Indian Rupee',
  IDR: 'Indonesian Rupiah',
  PHP: 'Philippine Peso',
  BRL: 'Brazilian Real',
  MXN: 'Mexican Peso',
  COP: 'Colombian Peso',
  ARS: 'Argentine Peso',
  CLP: 'Chilean Peso',
  PEN: 'Peruvian Sol',
  ZAR: 'South African Rand',
  MAD: 'Moroccan Dirham',
  EGP: 'Egyptian Pound',
  SAR: 'Saudi Riyal',
  QAR: 'Qatari Riyal',
  OMR: 'Omani Rial',
  BHD: 'Bahraini Dinar',
  KWD: 'Kuwaiti Dinar',
  CNY: 'Chinese Yuan',
  TWD: 'Taiwan Dollar',
  RUB: 'Russian Ruble',
}

export const CURRENCIES = Object.entries(CURRENCY_LABELS)
  .map(([code, name]) => ({ value: code, label: `${name} (${code})` }))
  .sort((a, b) => a.label.localeCompare(b.label))

// --- Languages (derived from next-intl routing.locales + iso-639-1) ---
import ISO6391 from 'iso-639-1'
import { routing } from '@/i18n/routing'

export const LANGUAGES = routing.locales.map((locale) => ({
  value: locale,
  label: ISO6391.getNativeName(locale) || locale,
}))

// Code → English name (e.g. { en: 'English', it: 'Italian', el: 'Greek' })
export const LANGUAGE_ENGLISH_NAMES: Record<string, string> = Object.fromEntries(
  routing.locales.map((l) => [l, ISO6391.getName(l) || l])
)

// Code → native name (e.g. { en: 'English', it: 'Italiano', el: 'Ελληνικά' })
export const LANGUAGE_NATIVE_NAMES: Record<string, string> = Object.fromEntries(
  routing.locales.map((l) => [l, ISO6391.getNativeName(l) || l])
)

// For inbox/editor translation dropdowns (with 'original' option)
export const TRANSLATE_LANGUAGES = [
  { value: 'original', label: 'Original' },
  ...routing.locales.map((l) => ({ value: l, label: ISO6391.getNativeName(l) || l })),
]

// For components that render SelectItems with i18n keys
export const LOCALE_I18N_KEYS: Record<string, string> = {
  en: 'langEnglish',
  zh: 'langChinese',
  hi: 'langHindi',
  es: 'langSpanish',
  fr: 'langFrench',
  ar: 'langArabic',
  bn: 'langBengali',
  pt: 'langPortuguese',
  ru: 'langRussian',
  ja: 'langJapanese',
  it: 'langItalian',
  he: 'langHebrew',
  de: 'langGerman',
  el: 'langGreek',
  pl: 'langPolish',
}

// --- Country → UI language mapping (only our supported locales) ---
const COUNTRY_LANGUAGE: Record<string, string> = {
  // English
  US: 'en',
  GB: 'en',
  AU: 'en',
  CA: 'en',
  NZ: 'en',
  IE: 'en',
  ZA: 'en',
  SG: 'en',
  // Spanish
  ES: 'es',
  MX: 'es',
  AR: 'es',
  CO: 'es',
  CL: 'es',
  PE: 'es',
  EC: 'es',
  VE: 'es',
  CU: 'es',
  DO: 'es',
  GT: 'es',
  HN: 'es',
  SV: 'es',
  NI: 'es',
  CR: 'es',
  PA: 'es',
  UY: 'es',
  PY: 'es',
  BO: 'es',
  PR: 'es',
  // French
  FR: 'fr',
  BE: 'fr',
  CH: 'fr',
  LU: 'fr',
  MC: 'fr',
  SN: 'fr',
  CI: 'fr',
  ML: 'fr',
  // Portuguese
  PT: 'pt',
  BR: 'pt',
  AO: 'pt',
  MZ: 'pt',
  // Italian
  IT: 'it',
  SM: 'it',
  VA: 'it',
  // Russian
  RU: 'ru',
  BY: 'ru',
  KZ: 'ru',
  KG: 'ru',
  // Arabic
  SA: 'ar',
  AE: 'ar',
  EG: 'ar',
  MA: 'ar',
  DZ: 'ar',
  TN: 'ar',
  IQ: 'ar',
  JO: 'ar',
  LB: 'ar',
  KW: 'ar',
  QA: 'ar',
  BH: 'ar',
  OM: 'ar',
  LY: 'ar',
  SD: 'ar',
  YE: 'ar',
  // Chinese
  CN: 'zh',
  TW: 'zh',
  HK: 'zh',
  MO: 'zh',
  // Japanese
  JP: 'ja',
  // Hindi
  IN: 'hi',
  // Bengali
  BD: 'bn',
  // Hebrew
  IL: 'he',
  // Polish
  PL: 'pl',
}

// --- Country defaults (timezone + currency + language) ---

// Override for countries where the alphabetical first timezone is wrong (e.g. US → Adak instead of New York)
const PREFERRED_TIMEZONE: Record<string, string> = {
  US: 'America/New_York',
  AU: 'Australia/Sydney',
  BR: 'America/Sao_Paulo',
  CA: 'America/Toronto',
  RU: 'Europe/Moscow',
  MX: 'America/Mexico_City',
  ID: 'Asia/Jakarta',
  CL: 'America/Santiago',
  CN: 'Asia/Shanghai',
  EC: 'America/Guayaquil',
  ES: 'Europe/Madrid',
  GL: 'America/Nuuk',
  KI: 'Pacific/Tarawa',
  MN: 'Asia/Ulaanbaatar',
  NZ: 'Pacific/Auckland',
  PF: 'Pacific/Tahiti',
  PG: 'Pacific/Port_Moresby',
  PT: 'Europe/Lisbon',
}

/** Get the default timezone, currency, and language for a country code */
export function getCountryDefaults(countryCode: string): {
  timezone: string
  currency: string
  language: string | null
} | null {
  const timezones = getTimezonesForCountry(countryCode)
  if (!timezones?.length) {
    return null
  }

  const timezone = PREFERRED_TIMEZONE[countryCode] || timezones[0].name
  const currency = (countryToCurrency as Record<string, string>)[countryCode] || 'USD'
  const language = COUNTRY_LANGUAGE[countryCode] || null

  return { timezone, currency, language }
}
